import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Stack, CircularProgress,
  Paper, IconButton, Tooltip, Snackbar, Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ControlsGrid from './ControlsGrid';
import { useDispatch, useSelector } from 'react-redux';
import { downloadComplianceReport, clearAllComplianceMessages } from '../../../store/compliance/complianceSlice';
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, tableShellSx } from '../AuditHub/theme';

// "audit" matches the Audit Hub pages: gray shell, white section cards, 13px figures.
const VARIANTS = {
  default: {
    shell: { border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
    header: { p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' },
    title: { fontWeight: 600, color: '#1a1a1a' },
    list: { p: 2, gap: 2 },
    border: '#e0e0e0',
    radius: '8px',
    hover: '#f8f9fa',
    metricSize: 15,
  },
  audit: {
    shell: { ...tableShellSx, p: { xs: 1.5, sm: 2 } },
    header: { mb: 1.5 },
    title: { fontWeight: 700, fontSize: '1rem', color: AH_TEXT },
    list: { gap: 1.25 },
    border: AH_BORDER,
    radius: '12px',
    hover: AH_TRACK,
    metricSize: 13,
  },
};

const ControlsCategoriesView = ({
  controls = [],
  onResourceClick,
  onSectionSelect,
  selectedSection,
  sectionControls,
  isLoadingControls,
  controlsError,
  compliance,
  runId,
  accountNumber,
  variant = 'default',
}) => {
  const look = VARIANTS[variant] || VARIANTS.default;
  const isAudit = look === VARIANTS.audit;
  const [expandedCategory, setExpandedCategory] = useState(null);
  const dispatch = useDispatch();
  const {
    isDownloadingComplianceReport,
    downloadComplianceReportMessage,
    downloadComplianceReportError
  } = useSelector((state) => state.compliance);

  // Prepare categories from controls array
  const categories = useMemo(() => {
    if (!Array.isArray(controls)) return [];
    return controls.map((category) => ({
      name: category.Section,
      path: category.SectionPath,
      metrics: {
        total: category.TotalControls,
        passed: category.PassedControls,
        failed: category.FailedControls,
        manual: category.ManualControls,
      },
    }));
  }, [controls]);

  const handleCategoryChange = (categoryPath) => (event, isExpanded) => {
    setExpandedCategory(isExpanded ? categoryPath : null);
    if (isExpanded) {
      onSectionSelect(categoryPath);
    }
  };

  return (
    <Paper elevation={0} sx={look.shell}>
      {/* Header Bar */}
      <Box
        sx={{
          ...look.header,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={look.title}>
            Compliance Controls
          </Typography>
          {isAudit ? (
            <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
              Expand a section to review its controls.
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Accordions Container */}
      <Box sx={{ ...look.list, display: 'flex', flexDirection: 'column' }}>
        {categories.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: AH_MUTED, py: 1 }}>Pending Automated Scan Completion</Typography>
        ) : null}
        {categories.map((category) => (
          <Accordion
            key={category.path}
            expanded={expandedCategory === category.path}
            onChange={handleCategoryChange(category.path)}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: `1px solid ${look.border}`,
              borderRadius: `${look.radius} !important`,
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
              },
            }}
          >
            <AccordionSummary
              sx={{
                backgroundColor: '#ffffff',
                borderBottom: `1px solid ${look.border}`,
                '&:hover': { backgroundColor: look.hover },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {expandedCategory === category.path ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: isAudit ? '0.9375rem' : '1rem', flex: 1, ...(isAudit ? { color: AH_TEXT } : null) }}>
                  {category.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    minWidth: 340,
                    gap: 0,
                  }}
                >
                  <Box sx={{ minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#495057', fontWeight: 500, fontSize: look.metricSize, width: '100%', whiteSpace: 'nowrap' }}>
                      Total Controls:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.total}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, fontSize: look.metricSize, width: '100%' }}>
                      Passed:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.passed}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500, fontSize: look.metricSize, width: '100%' }}>
                      Failed:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.failed}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500, fontSize: look.metricSize, width: '100%' }}>
                      Manual:
                      <span style={{ fontFamily: "inherit", fontWeight: 500, marginLeft: 6, minWidth: 20, display: 'inline-block', textAlign: 'right' }}>{category.metrics.manual}</span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {expandedCategory === category.path ? (
                isLoadingControls ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : controlsError ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="error">Error loading controls. Please try again.</Typography>
                  </Box>
                ) : sectionControls && sectionControls.controlsListJson ? (
                  <ControlsGrid
                    controls={sectionControls.controlsListJson}
                    onResourceClick={onResourceClick}
                    compliance={compliance}
                    accountNumber={accountNumber}
                  />
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">No controls found for this section.</Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">Expand to view controls</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

export default ControlsCategoriesView; 